import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';



/**
 * Base configuration for all API instances
 */
const baseConfig = {
  baseURL: import.meta.env.VITE_API_BASE || '',
  timeout: 60 * 1000,
  headers: {
    'Accept': 'application/json',
  },
};

/**
 * Called when any response signals maintenance mode (503 + X-Maintenance-Mode: true).
 * Apps register a handler via setMaintenanceModeHandler(fn).
 */
let maintenanceModeHandler: (() => void) | null = null;

export const setMaintenanceModeHandler = (handler: () => void) => {
  maintenanceModeHandler = handler;
};

const isMaintenanceResponse = (error: AxiosError): boolean => {
  return (
    error.response?.status === 503 &&
    error.response.headers?.['x-maintenance-mode'] === 'true'
  );
};

/**
 * Resident-restriction live refresh, driven by `X-Session-State-Version` on
 * authenticated responses.
 */
let restrictionRefreshHandler: (() => void) | null = null;
let restrictionBlockedHandler: (() => void) | null = null;

// Highest version seen — dedups concurrent responses. null = no baseline yet.
let observedStateVersion: number | null = null;
// Highest version actually reflected in the store. Lags `observed` while a
// refresh is in flight, and is what `observed` rolls back to if one fails.
let appliedStateVersion: number | null = null;

export const setRestrictionRefreshHandler = (handler: (() => void) | null) => {
  restrictionRefreshHandler = handler;
};

export const setRestrictionBlockedHandler = (handler: (() => void) | null) => {
  restrictionBlockedHandler = handler;
};

/** A refresh succeeded — this version is now reflected in the store. */
export const setAppliedStateVersion = (version: number | string | null | undefined) => {
  const parsed = version == null ? NaN : Number(version);
  if (!Number.isFinite(parsed)) return;

  appliedStateVersion = parsed;
  if (observedStateVersion == null || parsed > observedStateVersion) {
    observedStateVersion = parsed;
  }
};

/**
 * A refresh failed — reopen the gate so the next response carrying that version
 * retries, instead of it being treated as already applied.
 */
export const reportRestrictionRefreshFailed = () => {
  observedStateVersion = appliedStateVersion;
};

/** Clear all restriction wiring — call on logout / session reset. */
export const resetRestrictionState = () => {
  observedStateVersion = null;
  appliedStateVersion = null;
  restrictionRefreshHandler = null;
  restrictionBlockedHandler = null;
};

const observeStateVersion = (rawHeader: unknown) => {
  if (rawHeader == null) return;

  const incoming = Number(rawHeader);
  if (!Number.isFinite(incoming)) return;

  if (observedStateVersion == null) {
    observedStateVersion = incoming;
    return;
  }

  // Strictly greater, never `!==`: responses can complete out of order.
  if (incoming <= observedStateVersion) return;

  // Adopt before dispatching so concurrent responses on this same version
  // don't each queue a refresh.
  observedStateVersion = incoming;
  restrictionRefreshHandler?.();
};

const isRestrictionError = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  (error.response?.data as { code?: string } | undefined)?.code ===
    'RESIDENT_RESTRICTED';

/**
 * Token resolver used by the authenticated client. Defaults to localStorage key 'app_token'.
 * Apps may override via setTokenResolver(fn).
 */
let tokenResolver: () => string | null = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('app_token');
    }
  } catch {}
  return null;
};

export const setTokenResolver = (resolver: () => string | null) => {
  tokenResolver = resolver;
};

/**
 * Create an unauthenticated Axios instance for public endpoints
 */
const createUnauthenticatedInstance = (): AxiosInstance => {
  const instance = axios.create(baseConfig);

  // Ensure FormData requests don't get JSON content-type
  instance.interceptors.request.use(
    (config) => {
      if (config.data instanceof FormData) {
        if (config.headers) {
          delete (config.headers as any)['Content-Type'];
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for handling common error cases
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      handleApiError(error);
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Create an authenticated Axios instance for protected endpoints
 */
const createAuthenticatedInstance = (): AxiosInstance => {
  const instance = axios.create(baseConfig);

  // Request interceptor for adding auth token
  instance.interceptors.request.use(
    (config) => {
      // Ensure FormData requests don't get JSON content-type
      if (config.data instanceof FormData) {
        if (config.headers) {
          delete (config.headers as any)['Content-Type'];
        }
      }
      const token = tokenResolver();
      if (token) {
        config.headers = config.headers || {};
        // API expects custom 'token' header
        (config.headers as any).token = token;
        // Identify client type (kept for parity with mobile)
        (config.headers as any).client = 'MOBILE';
      } else {
        // If no token is available, reject the request
        return Promise.reject(new Error('No authentication token available'));
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor: watch the session-state version, then handle errors
  instance.interceptors.response.use(
    (response) => {
      observeStateVersion(response.headers?.['x-session-state-version']);
      return response;
    },
    (error: AxiosError) => {
      observeStateVersion(error.response?.headers?.['x-session-state-version']);
      handleApiError(error);
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Handle common API errors
 */
const handleApiError = (error: AxiosError) => {
  if (isMaintenanceResponse(error)) {
    maintenanceModeHandler?.();
    return;
  }

  // A write blocked by a resident restriction — never retried, never a logout.
  if (isRestrictionError(error)) {
    restrictionBlockedHandler?.();
    return;
  }

  if (error.response) {
    // Handle specific status codes
    switch (error.response.status) {
      case 401:
        // Handle unauthorized (token expired, invalid credentials)
        // store.dispatch(logout());
        break;
      case 403:
        // Handle forbidden
        console.error('Forbidden resource');
        break;
      case 500:
        // Handle server error
        console.error('Server error');
        break;
    }
  } else if (error.request) {
    // Handle network error
    console.error('Network error - no response received', error);
  } else {
    // Handle other errors
    console.error('Error:', error.message);
  }
};

// Create the API instances
const unauthenticatedInstance = createUnauthenticatedInstance();
const authenticatedInstance = createAuthenticatedInstance();

/**
 * Unauthenticated API client for public endpoints (login, signup, etc.)
 */
export const unauthenticatedApiClient = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await unauthenticatedInstance.get<T>(url, config);
    return response.data;
  },

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await unauthenticatedInstance.post<T>(url, data, config);
    return response.data;
  },

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await unauthenticatedInstance.put<T>(url, data, config);
    return response.data;
  },

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await unauthenticatedInstance.patch<T>(url, data, config);
    return response.data;
  },

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await unauthenticatedInstance.delete<T>(url, config);
    return response.data;
  },
};

/**
 * Authenticated API client for protected endpoints (user data, groups, etc.)
 */
export const authenticatedApiClient = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await authenticatedInstance.get<T>(url, config);
    return response.data;
  },

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await authenticatedInstance.post<T>(url, data, config);
    return response.data;
  },

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await authenticatedInstance.put<T>(url, data, config);
    return response.data;
  },

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await authenticatedInstance.patch<T>(url, data, config);
    return response.data;
  },

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await authenticatedInstance.delete<T>(url, config);
    return response.data;
  },
};
